import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import React, { useState } from 'react';
import { renderToString } from 'react-dom/server';
import { useNavigate, useParams } from 'react-router-dom';

import ColorBlock from '@components/common/ColorBlock/ColorBlock';
import SkeletonBlock from '@components/common/Skeleton/SkeletonBlock';
import Tabs from '@components/uikit/Tabs/Tabs';

import useTranslation from '@hooks/useTranslation';

import { selectAppLocationSelectValue } from '@store/useAppStore';

import type { TOverviewFilter } from '../../model/assetHealthOverview';
import { useAssetHealthOverviewQuery } from '../../services/useAssetHealthOverviewQuery';
import {
  selectAssetHealthUrgency,
  useAssetHealthOverviewStoreActions,
} from '../../store/useAssetHealthOverviewFilterStorePersist';
import NoData from '../NoData/NoData';
import StatisticWrapper from '../StatisticWrapper/StatisticWrapper';
import style from './style.module.scss';

interface ExtendedChart extends Highcharts.Chart {
  centerText: Highcharts.SVGElement;
}

const AssetHealthOverview = () => {
  const { t } = useTranslation();
  const { customerId } = useParams();
  const urgency = selectAssetHealthUrgency();
  const navigate = useNavigate();

  const { setAssetHealthUrgency } = useAssetHealthOverviewStoreActions();

  const [overviewFilter, setOverviewFilter] = useState<TOverviewFilter>('sensors');

  const locationSelectValue = selectAppLocationSelectValue();

  const { data, isPlaceholderData } = useAssetHealthOverviewQuery({
    locationIds:
      locationSelectValue.level_name === 'allFacilities' ? undefined : locationSelectValue?.filterIds,
  });

  const seriesData = data?.[overviewFilter];

  const options: Highcharts.Options = {
    chart: {
      className: style.sensorsChart,
      events: {
        render: function () {
          const chart = this as ExtendedChart;
          const series = chart.series[0];
          const textX = series.center[0] + chart.plotLeft;
          const textY = series.center[1] + chart.plotTop;

          if (chart.centerText) chart.centerText.destroy();

          const { critical_regular_ais_count, high_regular_ais_count, standard_regular_ais_count } =
            seriesData!;
          const total = critical_regular_ais_count + high_regular_ais_count + standard_regular_ais_count;

          chart.centerText = chart.renderer
            .text(
              `${total} <br/> <span class="${style.centerTitle}">${t('Total Action Items')}</span>`,
              textX,
              textY,
              true,
            )
            .addClass(style.centerText)
            .add();

          chart.centerText.attr({
            x: textX - chart.centerText.getBBox().width / 2,
            y: textY - chart.centerText.getBBox().height / 4,
          });
        },
      },
    },
    title: {
      text: '',
    },
    tooltip: {
      format: '{point.name}: <b>{point.y}</b>',
    },
    legend: {
      enabled: true,
      symbolRadius: 0,
      symbolWidth: 0,
      symbolHeight: 0,
      align: 'center',
      useHTML: true,
      labelFormatter: function () {
        const { name, color } = this;
        return renderToString(
          <div className={style.legendLabel}>
            <ColorBlock color={color as string} width={12} height={12} />
            <span>{name}</span>
          </div>,
        );
      },
    },
    credits: { enabled: false },
    plotOptions: {
      series: {
        showInLegend: true,
        dataLabels: [
          {
            enabled: true,
            formatter: function () {
              return this.y! > 0 ? this.y : null;
            },
            className: style.dataLabel,
          },
        ],
      },
    },
    accessibility: { enabled: false },
    series: [
      {
        type: 'pie',
        name: '',
        slicedOffset: 0,
        innerSize: '75%',
        center: ['50%', '50%'],
        size: '80%',
        cursor: 'pointer',
        data: [
          {
            name: t('Critical'),
            y: seriesData?.critical_regular_ais_count,
            color: '#F24F52',
            sliced: true,
            events: {
              click: function () {
                navigate(`/customer/${customerId}/executive-summary/asset-health-details`);
                setAssetHealthUrgency({ value: 3, label: 'Critical' });
              },
            },
          },
          {
            name: t('High'),
            y: seriesData?.high_regular_ais_count,
            color: '#F89B11',
            sliced: true,
            events: {
              click: function () {
                navigate(`/customer/${customerId}/executive-summary/asset-health-details`);
                setAssetHealthUrgency({ value: 2, label: 'High' });
              },
            },
          },
          {
            name: t('Standard'),
            y: seriesData?.standard_regular_ais_count,
            color: '#FDC526',
            sliced: true,
            events: {
              click: function () {
                navigate(`/customer/${customerId}/executive-summary/asset-health-details`);
                setAssetHealthUrgency({ value: 1, label: 'Standard' });
              },
            },
          },
          {
            name: t('No Action Items'),
            y: seriesData?.no_ais_count,
            color: '#42AD7E',
            sliced: true,
            events: {
              click: function () {
                navigate(`/customer/${customerId}/executive-summary/asset-health-details`);
              },
            },
          },
        ],
      },
    ],
  };

  return (
    <StatisticWrapper
      title={t('Asset Health Overview')}
      rightElement={
        <Tabs
          id="asset-health-overview-type"
          value={overviewFilter}
          tabsItems={[
            { value: 'sensors', label: t('By Sensors') },
            { value: 'equipment', label: t('By Equipment') },
          ]}
          type={'woHelperText'}
          onChange={setOverviewFilter}
        />
      }
      link={`/customer/${customerId}/executive-summary/asset-health-details`}
    >
      <div className={style.fullHeight}>
        {isPlaceholderData && <SkeletonBlock className={style.skeleton} />}
        {!isPlaceholderData && !!seriesData && (
          <HighchartsReact
            highcharts={Highcharts}
            options={options}
            containerProps={{ className: 'printHighchartsChart' }}
          />
        )}
        {!isPlaceholderData && !seriesData && <NoData />}
      </div>
    </StatisticWrapper>
  );
};

export default AssetHealthOverview;
